import type { SupportWidgetConfig } from './components/support/types';

export const kogSupportConfig: SupportWidgetConfig = {
  productName: 'KOG CRM',
  productId: 'kog',
  hotkey: '?',
  tabs: ['tutorials', 'docs', 'releases'],
  triggerPosition: 'bottom-right',
  defaultWidth: 400,
  zIndex: 55,

  charlotte: {
    endpoint: (import.meta.env.VITE_CHARLOTTE_URL || 'https://charlotte.netrunsystems.com:8080') + '/ws/voice',
    systemPrompt:
      'You are Charlotte, the AI assistant for KOG CRM by Netrun Systems. ' +
      'Help users navigate the CRM, manage contacts, track deals, and use campaign intelligence features. ' +
      'Be concise, action-oriented, and reference specific UI elements when guiding users.',
    placeholder: 'Ask Charlotte anything about KOG...',
  },

  theme: {
    bgPanel: '#0f0f14',
    bgSurface: '#1a1a24',
    accentPrimary: '#00e676',
    accentSecondary: '#7b2cbf',
    textPrimary: '#e8e8e8',
    textSecondary: '#8a8a9a',
    border: '#2a2a3a',
    overlayBg: 'rgba(0, 0, 0, 0.75)',
    borderRadius: '10px',
    fontFamily:
      "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },

  /* ================================================================ */
  /*  12 KOG Tutorials                                                 */
  /* ================================================================ */
  tutorials: [
    /* ---- Tutorial 1: Add Your First Contact ---- */
    {
      id: 'kog-add-contact',
      title: 'Add Your First Contact',
      description: 'Learn how to create a new contact in KOG CRM using the quick-add form.',
      category: 'Getting Started',
      duration: '2 min',
      route: '/contacts',
      steps: [
        {
          target: 'nav button:nth-child(1)',
          title: 'Open Contacts',
          content: 'Tap the Contacts tab in the bottom bar to see your contact list.',
          placement: 'top',
        },
        {
          target: '[data-support-widget] ~ div .fixed.z-50 button.rounded-full.w-14',
          title: 'Tap the + Button',
          content: 'Tap the green floating action button to see quick actions.',
          placement: 'left',
          allowInteraction: true,
        },
        {
          target: '.fixed.z-50 .flex.items-center.gap-3:nth-child(3) button',
          title: 'Select "Add Contact"',
          content: 'Choose "Add Contact" from the speed dial menu.',
          placement: 'left',
          allowInteraction: true,
        },
        {
          target: 'input[placeholder*="First"]',
          title: 'Enter First Name',
          content: 'Type the contact\'s first name in this field.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: 'input[placeholder*="Email"], input[type="email"]',
          title: 'Add Email Address',
          content: 'Enter their email so you can reach them from KOG.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: 'button[type="submit"], form button:last-of-type',
          title: 'Save the Contact',
          content: 'Tap Save to create the contact. It will appear in your contact list.',
          placement: 'top',
          allowInteraction: true,
        },
      ],
    },

    /* ---- Tutorial 2: Log a Meeting or Call ---- */
    {
      id: 'kog-log-activity',
      title: 'Log a Meeting or Call',
      description: 'Record meetings, calls, and notes against your contacts.',
      category: 'Getting Started',
      duration: '2 min',
      route: '/activities',
      steps: [
        {
          target: '.fixed.z-50 button.rounded-full.w-14',
          title: 'Open Quick Actions',
          content: 'Tap the green + button to access quick actions.',
          placement: 'left',
          allowInteraction: true,
        },
        {
          target: '.fixed.z-50 .flex.items-center.gap-3:nth-child(2) button',
          title: 'Select "Log Activity"',
          content: 'Choose "Log Activity" from the speed dial menu.',
          placement: 'left',
          allowInteraction: true,
        },
        {
          target: 'select, [role="listbox"]',
          title: 'Choose Activity Type',
          content: 'Select the type: Meeting, Call, Note, or Task.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: 'input[placeholder*="Search"], input[placeholder*="contact"]',
          title: 'Link a Contact',
          content: 'Search for the contact this activity relates to.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: 'textarea, input[placeholder*="description"], input[placeholder*="note"]',
          title: 'Add Details',
          content: 'Describe what happened or what needs to be done.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: 'button[type="submit"], form button:last-of-type',
          title: 'Save Activity',
          content: 'Tap Save to log the activity. It will appear in your activity feed.',
          placement: 'top',
          allowInteraction: true,
        },
      ],
    },

    /* ---- Tutorial 3: Manage Your Sales Pipeline ---- */
    {
      id: 'kog-pipeline',
      title: 'Manage Your Sales Pipeline',
      description: 'Track deals through stages from discovery to close.',
      category: 'Sales',
      duration: '3 min',
      route: '/pipeline',
      steps: [
        {
          target: 'nav button:nth-child(2)',
          title: 'Go to Pipeline',
          content: 'Tap the Pipeline tab to see your deal board.',
          placement: 'top',
        },
        {
          target: '.flex.gap-4.overflow-x-auto, [data-pipeline-board]',
          title: 'Pipeline Stages',
          content:
            'Your deals flow through stages: Discovery, Qualification, Proposal, Negotiation, and Closed. ' +
            'Each column represents a stage.',
          placement: 'bottom',
        },
        {
          target: '[data-deal-card], .rounded-lg.p-4.cursor-pointer',
          title: 'View a Deal',
          content: 'Tap any deal card to see its details, contacts, and activity history.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: '[data-deal-card], .rounded-lg.p-4.cursor-pointer',
          title: 'Move Deals Between Stages',
          content:
            'On mobile, swipe a deal card left or right to move it between stages. ' +
            'On desktop, drag and drop.',
          placement: 'bottom',
        },
        {
          target: '.fixed.z-50 button.rounded-full.w-14',
          title: 'Create a New Deal',
          content: 'Use the + button and select "New Deal" to add an opportunity to your pipeline.',
          placement: 'left',
          allowInteraction: true,
        },
      ],
    },

    /* ---- Tutorial 4: Chat with Charlotte AI ---- */
    {
      id: 'kog-charlotte-chat',
      title: 'Chat with Charlotte AI',
      description: 'Use the AI assistant to get instant answers and CRM insights.',
      category: 'AI Features',
      duration: '2 min',
      route: '/voice',
      steps: [
        {
          target: 'nav button:nth-child(4)',
          title: 'Open AI Chat',
          content: 'Tap the AI Chat tab to access Charlotte, your sales assistant.',
          placement: 'top',
        },
        {
          target: 'input[placeholder*="Type"], input[placeholder*="message"], textarea',
          title: 'Type a Question',
          content:
            'Type a question like "show me recent contacts" or "what deals are in negotiation?"',
          placement: 'top',
          allowInteraction: true,
        },
        {
          target: 'button[aria-label*="Send"], button:has(svg):last-of-type',
          title: 'Send Your Message',
          content: 'Tap send or press Enter to submit your question.',
          placement: 'left',
          allowInteraction: true,
        },
        {
          target: '.overflow-y-auto .rounded-lg.p-4, [data-transcript]',
          title: 'Read the Response',
          content:
            'Charlotte will respond with relevant information from your CRM data. ' +
            'She can look up contacts, summarize pipeline status, and draft outreach.',
          placement: 'bottom',
        },
        {
          target: 'input[placeholder*="Type"], input[placeholder*="message"], textarea',
          title: 'Try a Command',
          content:
            'Try asking: "show me recent contacts", "draft a follow-up email for [name]", ' +
            'or "what is my pipeline value?"',
          placement: 'top',
        },
      ],
    },

    /* ---- Tutorial 5: Use Voice Mode ---- */
    {
      id: 'kog-voice-mode',
      title: 'Use Voice Mode',
      description: 'Talk to Charlotte using your microphone for hands-free CRM interaction.',
      category: 'AI Features',
      duration: '2 min',
      route: '/voice',
      steps: [
        {
          target: 'nav button:nth-child(4)',
          title: 'Go to AI Chat',
          content: 'Navigate to the AI Chat page where Charlotte lives.',
          placement: 'top',
        },
        {
          target: 'button[aria-label*="microphone"], button[aria-label*="voice"], button:has(svg[viewBox] path[d*="M12 2a3"])',
          title: 'Tap the Microphone',
          content:
            'Tap the microphone button to start voice mode. Grant microphone access if prompted.',
          placement: 'top',
          allowInteraction: true,
        },
        {
          target: 'button[aria-label*="microphone"], button[aria-label*="voice"]',
          title: 'Speak Your Question',
          content: 'Speak naturally. Charlotte will transcribe and respond to your question.',
          placement: 'top',
        },
        {
          target: '.overflow-y-auto, [data-transcript-list]',
          title: 'Wait for the Response',
          content:
            'Charlotte will process your voice input and respond with both text and audio.',
          placement: 'bottom',
        },
        {
          target: 'button[aria-label*="Stop"], button[aria-label*="stop"]',
          title: 'Control Playback',
          content: 'Use the stop button to end voice mode when you are done.',
          placement: 'top',
          allowInteraction: true,
        },
      ],
    },

    /* ---- Tutorial 6: View Your Analytics ---- */
    {
      id: 'kog-analytics',
      title: 'View Your Analytics',
      description: 'Explore your CRM performance metrics and pipeline analytics.',
      category: 'Insights',
      duration: '2 min',
      route: '/analytics',
      steps: [
        {
          target: 'nav button:nth-child(5), a[href="/analytics"]',
          title: 'Navigate to Analytics',
          content: 'Tap "More" in the bottom bar, then find Analytics in the menu.',
          placement: 'top',
        },
        {
          target: '.grid, [data-kpi-cards]',
          title: 'Read Your KPIs',
          content:
            'The top row shows key performance indicators: total contacts, active deals, pipeline value, and conversion rate.',
          placement: 'bottom',
        },
        {
          target: '[data-chart], .recharts-responsive-container, svg.recharts-surface',
          title: 'Explore Pipeline Chart',
          content: 'The pipeline chart shows deal distribution across stages. Hover or tap for details.',
          placement: 'bottom',
        },
        {
          target: '[data-conversion], .rounded-lg:last-of-type',
          title: 'Check Conversion Rates',
          content: 'Review stage-to-stage conversion rates to identify bottlenecks in your sales process.',
          placement: 'top',
        },
      ],
    },

    /* ---- Tutorial 7: Search and Filter Contacts ---- */
    {
      id: 'kog-search-contacts',
      title: 'Search and Filter Contacts',
      description: 'Quickly find contacts using search and filters.',
      category: 'Getting Started',
      duration: '1 min',
      route: '/contacts',
      steps: [
        {
          target: 'nav button:nth-child(1)',
          title: 'Go to Contacts',
          content: 'Navigate to the Contacts page.',
          placement: 'top',
        },
        {
          target: 'input[placeholder*="Search"], input[type="search"]',
          title: 'Open the Search Bar',
          content: 'Tap the search bar at the top of the contacts list.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: 'input[placeholder*="Search"], input[type="search"]',
          title: 'Type a Name',
          content: 'Start typing a contact name. The list filters in real time as you type.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: '[data-contact-card], .rounded-lg.cursor-pointer, table tbody tr',
          title: 'Select a Contact',
          content: 'Tap a contact card to view their full profile, activity history, and deals.',
          placement: 'bottom',
          allowInteraction: true,
        },
      ],
    },

    /* ---- Tutorial 8: Campaign Management ---- */
    {
      id: 'kog-campaign-management',
      title: 'Campaign Management',
      description: 'Create campaigns, add contacts, and track your outreach funnel.',
      category: 'Sales',
      duration: '3 min',
      route: '/campaigns',
      steps: [
        {
          target: 'a[href="/campaigns"], nav button:has(svg)',
          title: 'Navigate to Campaigns',
          content: 'Open the Campaigns page from the sidebar or navigation menu.',
          placement: 'right',
        },
        {
          target: 'button:has(svg[viewBox] path[d*="M12 4"]), [data-create-campaign]',
          title: 'Create a New Campaign',
          content: 'Click the "New Campaign" button to set up a new outreach campaign.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: '[data-campaign-contacts], .campaign-contacts-list',
          title: 'Add Contacts to Campaign',
          content: 'Search and add contacts to your campaign. You can also import from smart lists.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: '[data-campaign-status], .campaign-status-badge',
          title: 'Track Campaign Status',
          content: 'Monitor each contact\'s status: Queued, Sent, Opened, Replied, or Converted.',
          placement: 'bottom',
        },
        {
          target: '[data-campaign-analytics], .recharts-responsive-container',
          title: 'View Analytics Funnel',
          content: 'The analytics funnel shows conversion rates at each stage of your campaign.',
          placement: 'bottom',
        },
      ],
    },

    /* ---- Tutorial 9: Voice-to-Text Search ---- */
    {
      id: 'kog-voice-search',
      title: 'Voice-to-Text Search',
      description: 'Use your microphone to search contacts, campaigns, and documents by voice.',
      category: 'Getting Started',
      duration: '1 min',
      route: '/contacts',
      steps: [
        {
          target: 'button[aria-label*="microphone"], button[aria-label*="voice-search"], .search-mic-btn',
          title: 'Click the Mic Icon',
          content: 'Click the microphone icon on the search bar to activate voice-to-text.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: 'input[placeholder*="Search"], input[type="search"]',
          title: 'Speak Your Search Query',
          content: 'Speak naturally — your words are transcribed into the search bar in real time.',
          placement: 'bottom',
        },
        {
          target: '[data-contact-card], .rounded-lg.cursor-pointer, table tbody tr',
          title: 'Results Filter Automatically',
          content: 'The list filters as your voice input is transcribed. Tap any result to open it.',
          placement: 'bottom',
        },
      ],
    },

    /* ---- Tutorial 10: Meeting Scheduling ---- */
    {
      id: 'kog-meeting-scheduling',
      title: 'Meeting Scheduling',
      description: 'Schedule phone, video, or in-person meetings directly from a campaign.',
      category: 'Sales',
      duration: '2 min',
      route: '/campaigns',
      steps: [
        {
          target: '[data-campaign-contacts], .campaign-contacts-list, table tbody tr',
          title: 'Open Campaign Contacts',
          content: 'Navigate to a campaign and view its contact list.',
          placement: 'bottom',
        },
        {
          target: 'button:has(svg), [data-schedule-btn]',
          title: 'Click Schedule Button',
          content: 'Click the Schedule button next to a contact to open the meeting scheduler.',
          placement: 'left',
          allowInteraction: true,
        },
        {
          target: 'select, [role="listbox"], [data-meeting-type]',
          title: 'Select Meeting Type',
          content: 'Choose the meeting type: Phone, Video, or In-Person.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: 'input[type="datetime-local"], input[type="date"], [data-meeting-datetime]',
          title: 'Set Date and Time',
          content: 'Pick the date and time for the meeting.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: 'button[type="submit"], [data-confirm-meeting]',
          title: 'Confirm the Meeting',
          content: 'Click Confirm to schedule the meeting. It will appear in the contact\'s activity timeline.',
          placement: 'top',
          allowInteraction: true,
        },
      ],
    },

    /* ---- Tutorial 11: Data Export ---- */
    {
      id: 'kog-data-export',
      title: 'Data Export',
      description: 'Export your contacts, organizations, and activities as CSV files.',
      category: 'Getting Started',
      duration: '1 min',
      route: '/contacts',
      steps: [
        {
          target: 'nav button:nth-child(1), a[href="/contacts"]',
          title: 'Navigate to Contacts or Organizations',
          content: 'Go to the Contacts or Organizations page where your data lives.',
          placement: 'top',
        },
        {
          target: 'button:has(svg), [data-export-btn], button[aria-label*="export"]',
          title: 'Click Export CSV',
          content: 'Click the "Export CSV" button in the toolbar above the list.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: 'body',
          title: 'File Downloads Automatically',
          content: 'Your browser will download a CSV file with all visible records. Open it in Excel, Google Sheets, or any spreadsheet app.',
          placement: 'bottom',
        },
      ],
    },

    /* ---- Tutorial 12: Two-Factor Authentication ---- */
    {
      id: 'kog-two-factor-auth',
      title: 'Two-Factor Authentication',
      description: 'Secure your account with TOTP-based two-factor authentication.',
      category: 'Getting Started',
      duration: '2 min',
      route: '/settings',
      steps: [
        {
          target: 'a[href="/settings"], nav button:has(svg)',
          title: 'Go to Settings',
          content: 'Open the Settings page from the sidebar or navigation menu.',
          placement: 'right',
        },
        {
          target: 'button[data-enable-2fa], button:has(svg), .mfa-toggle',
          title: 'Click Enable 2FA',
          content: 'Find the Security section and click "Enable 2FA" to start setup.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: '[data-qr-code], .qr-code-container, canvas, img[alt*="QR"]',
          title: 'Scan the QR Code',
          content: 'Open your authenticator app (Google Authenticator, Authy, etc.) and scan the QR code displayed on screen.',
          placement: 'bottom',
        },
        {
          target: 'input[placeholder*="code"], input[placeholder*="6-digit"], input[inputmode="numeric"]',
          title: 'Enter the 6-Digit Code',
          content: 'Type the 6-digit verification code from your authenticator app to confirm setup.',
          placement: 'bottom',
          allowInteraction: true,
        },
        {
          target: '.mfa-status, [data-mfa-active], .badge',
          title: 'MFA Is Now Active',
          content: 'Two-factor authentication is enabled. You will be prompted for a code on each login.',
          placement: 'bottom',
        },
      ],
    },
  ],

  /* ================================================================ */
  /*  KOG Documentation Sections                                       */
  /* ================================================================ */
  docs: [
    {
      id: 'contacts-management',
      title: 'Contacts Management',
      keywords: ['contact', 'add', 'edit', 'search', 'delete', 'import'],
      content: `# Contacts Management

KOG CRM is centered around your contacts. Here is how to manage them effectively.

## Adding Contacts

- Tap the **+** floating action button and select **Add Contact**
- Fill in the name, email, phone, company, and title
- Only first name is required — add more details later

## Editing Contacts

- Navigate to the contact's profile page
- Tap any field to edit it inline
- Changes save automatically

## Searching Contacts

- Use the search bar at the top of the Contacts page
- Search matches first name, last name, email, and company
- Results filter in real time as you type

## Lead Scores

Each contact has an AI-generated lead score (0-100) based on:
- Engagement frequency
- Activity recency
- Deal association
- Email interaction history`,
    },
    {
      id: 'pipeline-stages',
      title: 'Pipeline Stages',
      keywords: ['pipeline', 'deal', 'stage', 'sales', 'funnel', 'opportunity'],
      content: `# Pipeline Stages

Your deals flow through five stages in KOG CRM:

## Discovery
Initial contact made. You are researching the prospect and identifying potential fit.

## Qualification
You have confirmed budget, authority, need, and timeline (BANT). The prospect is a real opportunity.

## Proposal
You have sent a formal proposal or statement of work. Waiting for review and feedback.

## Negotiation
Terms are being discussed. Pricing, scope, or timeline adjustments may be in progress.

## Closed
The deal has been won (or lost). Closed-won deals become active customers.

## Moving Deals

- **Mobile**: Swipe a deal card left/right to move between stages
- **Desktop**: Drag and drop deal cards between columns
- **Manual**: Open the deal and change the stage dropdown`,
    },
    {
      id: 'activities',
      title: 'Activities',
      keywords: ['activity', 'meeting', 'call', 'note', 'task', 'log'],
      content: `# Activities

Activities track every interaction with your contacts.

## Activity Types

- **Meeting**: In-person or virtual meetings
- **Call**: Phone or video calls
- **Note**: Internal notes and observations
- **Task**: To-do items with optional due dates

## Logging Activities

1. Tap the **+** floating action button
2. Select **Log Activity**
3. Choose the type, link a contact, and add details
4. Tap Save

## Activity Feed

The Activities page shows a chronological feed of all logged activities. Filter by type or contact to find what you need.

## Automatic Tracking

Charlotte AI automatically logs voice interactions as activities, so your conversation history is always captured.`,
    },
    {
      id: 'charlotte-ai',
      title: 'Charlotte AI',
      keywords: ['charlotte', 'ai', 'assistant', 'voice', 'chat', 'gemini'],
      content: `# Charlotte AI

Charlotte is your AI-powered sales assistant, built on Google Gemini.

## What You Can Ask

- **Contact lookups**: "Show me recent contacts" or "Find contacts at Acme Corp"
- **Pipeline insights**: "What is my total pipeline value?" or "Which deals are in negotiation?"
- **Draft outreach**: "Draft a follow-up email for John Smith"
- **Activity summaries**: "What meetings do I have this week?"

## Text vs Voice Mode

- **Text**: Type your question in the input bar and press Enter
- **Voice**: Tap the microphone icon and speak naturally

## Tips for Better Results

- Be specific: "contacts added this week" works better than "show contacts"
- Use names: Charlotte can look up specific people in your CRM
- Ask for actions: "draft an email" or "create a task" to get actionable output

## RAG Knowledge Base

Charlotte queries the \`kog_sales\` knowledge base, which contains your CRM data, contact intelligence, and pipeline information. Responses are grounded in your real data.`,
    },
    {
      id: 'keyboard-shortcuts',
      title: 'Keyboard Shortcuts',
      keywords: ['keyboard', 'shortcut', 'hotkey', 'keys'],
      content: `# Keyboard Shortcuts

Speed up your workflow with these keyboard shortcuts (desktop only):

| Shortcut | Action |
|----------|--------|
| **?** | Toggle the support/help panel |
| **Escape** | Close any open modal or panel |
| **/** | Focus the global search bar |
| **n** | New contact (when on Contacts page) |
| **Ctrl+Enter** | Submit form / send message |

## In the Support Panel

| Shortcut | Action |
|----------|--------|
| **Tab** | Cycle through panel elements |
| **Escape** | Close the support panel |
| **Arrow keys** | Navigate tutorial steps |`,
    },
    {
      id: 'mobile-tips',
      title: 'Mobile Tips',
      keywords: ['mobile', 'tablet', 'touch', 'responsive', 'gesture', 'bottom bar'],
      content: `# Mobile Tips

KOG CRM is designed mobile-first. Here are tips for the best experience.

## Bottom Tab Bar

The bottom navigation bar gives you quick access to:
- **Contacts** — Your contact list
- **Pipeline** — Deal board
- **Activities** — Activity feed
- **AI Chat** — Charlotte voice/text assistant
- **More** — Dashboard, Analytics, Documents, Settings

## Floating Action Button (FAB)

The green **+** button in the bottom-right corner is your shortcut to:
- Add a new contact
- Log an activity
- Create a new deal

Tap it to expand the speed dial menu.

## Gestures

- **Pull to refresh** on list pages to reload data
- **Swipe left/right** on pipeline deal cards to move between stages
- **Long press** on a contact card for quick actions

## Safe Area

KOG respects the safe area on iPhones with notches. The bottom tab bar and FAB automatically adjust for the home indicator.`,
    },
    {
      id: 'campaigns',
      title: 'Campaigns',
      keywords: ['campaign', 'outreach', 'funnel', 'analytics', 'sequence', 'contacts'],
      content: `# Campaigns

Campaigns let you organize and track multi-step outreach to groups of contacts.

## Creating a Campaign

1. Navigate to **Campaigns** from the sidebar
2. Click **New Campaign** and give it a name and description
3. Choose the campaign type: Email, SMS, Phone, or Multi-channel

## Adding Contacts

- Search and add individual contacts from the campaign detail page
- Import contacts from a smart list or CSV
- Copy contacts from an existing campaign using cross-campaign copy

## Tracking the Funnel

Each contact in a campaign progresses through stages:
- **Queued** — Added but not yet contacted
- **Sent** — Message delivered
- **Opened** — Recipient opened the message
- **Replied** — Recipient responded
- **Converted** — Desired action completed

## Campaign Analytics

The analytics panel shows:
- Conversion rates at each funnel stage
- Response rate over time
- Top-performing contacts and messages`,
    },
    {
      id: 'sms-voice',
      title: 'SMS & Voice',
      keywords: ['sms', 'voice', 'telnyx', 'text', 'call', 'auto-reply', 'inbound', 'outbound'],
      content: `# SMS & Voice

KOG supports multi-provider SMS and voice communication via Telnyx integration.

## Sending SMS

- Open a contact profile and tap the SMS icon
- Type your message and tap Send
- Messages are sent via Telnyx and delivery status is tracked automatically

## Inbound SMS

- Inbound messages from contacts are routed to the contact's activity timeline
- AI auto-reply can respond to common inquiries when enabled

## Voice Calls

- Click the phone icon on a contact profile to initiate a call
- Call duration and notes are logged as activities

## AI Auto-Reply

When enabled, Charlotte can automatically respond to inbound SMS with context-aware replies based on the contact's history and campaign status. Configure auto-reply rules in **Settings > Communication**.

## Outbound Campaigns

SMS and voice can be used as campaign channels. Set up sequences with scheduled sends and follow-up triggers.`,
    },
    {
      id: 'billing-plans',
      title: 'Billing & Plans',
      keywords: ['billing', 'plan', 'subscription', 'pricing', 'usage', 'metering', 'starter', 'professional', 'enterprise'],
      content: `# Billing & Plans

KOG offers three subscription tiers to match your team's needs.

## Plans

| Plan | Price | Contacts | Campaigns | SMS/mo |
|------|-------|----------|-----------|--------|
| **Starter** | $29/mo | 1,000 | 5 | 500 |
| **Professional** | $79/mo | 10,000 | Unlimited | 5,000 |
| **Enterprise** | $199/mo | Unlimited | Unlimited | 25,000 |

## Usage Metering

Your current usage is displayed in **Settings > Billing**:
- Contact count with plan limit and progress bar
- Campaign count (Starter plan only)
- SMS messages sent this billing period
- API calls (Enterprise plan)

## Changing Plans

1. Go to **Settings > Billing**
2. Click **Change Plan**
3. Select your new tier — upgrades take effect immediately, downgrades at next billing cycle

## Self-Service Registration

New users can sign up at the registration page. A tenant workspace is created automatically with the Starter plan.`,
    },
    {
      id: 'security',
      title: 'Security',
      keywords: ['security', 'mfa', '2fa', 'password', 'authentication', 'rate-limit', 'totp'],
      content: `# Security

KOG includes multiple layers of security to protect your CRM data.

## Two-Factor Authentication (MFA)

1. Go to **Settings > Security**
2. Click **Enable 2FA**
3. Scan the QR code with your authenticator app (Google Authenticator, Authy, 1Password)
4. Enter the 6-digit code to confirm
5. MFA is now required on every login

## Password Reset

- Click "Forgot Password" on the login page
- Enter your email to receive a reset link
- Links expire after 15 minutes

## Rate Limiting

KOG enforces rate limits to prevent abuse:
- Login attempts: 5 per minute per IP
- API requests: Based on your subscription plan
- Password reset: 3 requests per hour

## Session Management

- JWT tokens expire after 24 hours
- Refresh tokens are rotated on each use
- All active sessions can be revoked from **Settings > Security**`,
    },
  ],

  /* ================================================================ */
  /*  KOG Release Notes                                                */
  /* ================================================================ */
  releases: [
    {
      id: 'kog-v5.9.0',
      version: 'v5.9.0',
      date: 'March 29, 2026',
      title: 'Launch Ready — Full Customer Lifecycle',
      type: 'feature',
      body: `v5.9.0 delivers the full customer lifecycle — from self-service signup to billing.

- **Self-service registration** with automatic tenant workspace creation
- **Campaign management**: create, contacts, analytics funnel, cross-campaign copy
- **Multi-provider SMS** (Telnyx), Voice, and Email (AWS SES) adapters
- **Inbound automation**: public lead capture, newsletter signup, HubSpot webhooks
- **Meeting scheduling** with phone, video, and in-person options
- **Voice-to-text search** on Contacts, Campaigns, and Documents
- **Two-factor authentication** (TOTP MFA)
- **CSV data export** for contacts, organizations, and activities
- **Collapsible sidebar** with tablet-sized touch targets
- **Glassmorphism UI redesign** with recharts analytics
- **Billing plans**: Starter $29, Professional $79, Enterprise $199
- **Usage metering** with plan limits and progress bars`,
    },
  ],
};
